// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * Shared formatting helpers for clinical identifiers.
 *
 * The OwlRx mirror generates a SYNTHETIC NPI when PioneerRx sends a
 * prescriber without one (the `prescribers.npi` column is UNIQUE NOT NULL
 * so a placeholder is required). Synthetic values look like:
 *   - "px:57fc985973454"           — prefixed marker
 *   - "57fc98597345f3d7"           — 16 hex chars (older variant)
 *   - 32-hex-char GUID fingerprints
 *
 * These are developer-facing sentinels and must NEVER be shown to a
 * pharmacy user as if they were real NPIs. All UI display of NPI/DEA must
 * route through these helpers.
 */

const HEX_FINGERPRINT_RE = /^[0-9a-f]{16,32}$/i

export function isSyntheticNpi(npi: string | null | undefined): boolean {
  if (!npi) return false
  if (npi.startsWith('px:')) return true
  if (HEX_FINGERPRINT_RE.test(npi)) return true
  // Real NPI is exactly 10 digits. Anything else of any length that isn't
  // 10 pure digits we treat as not-a-real-NPI to be safe.
  if (npi.length === 10 && /^\d{10}$/.test(npi)) return false
  // For DEAs and state licenses, callers should use isValidDea/isValidStateLicense.
  // For raw NPI display, treat non-10-digit values as synthetic.
  return true
}

export function formatNpi(npi: string | null | undefined): string {
  if (!npi) return '—'
  if (isSyntheticNpi(npi)) return 'Not provided'
  return npi
}

// DEA: 2 letters + 7 digits (e.g. AB1234567). Anything else hides.
export function formatDea(dea: string | null | undefined): string {
  if (!dea) return '—'
  if (dea.startsWith('px:')) return 'Not provided'
  if (HEX_FINGERPRINT_RE.test(dea)) return 'Not provided'
  if (!/^[A-Z]{2}\d{7}$/i.test(dea.trim())) return dea  // show as-is if it doesn't match standard, but caller saw real value
  return dea
}

// ---------------------------------------------------------------------------
// Phone + DOB: platform-wide display normalization. ASP reported these render
// inconsistently across the system; the fix is one formatter each, used at
// EVERY display site (patients, prescribers, shipment labels, queues). These
// are pure display helpers — they never mutate stored data — and fall back to
// the raw value rather than hiding a real number/date they can't parse.
// ---------------------------------------------------------------------------

// US phone display: (XXX) XXX-XXXX. Strips a leading country-code 1; if the
// value isn't a recognizable 10-digit number it's shown as-is (trimmed).
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '—'
  const digits = String(phone).replace(/\D/g, '')
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (ten.length !== 10) return String(phone).trim() || '—'
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
}

// DOB display: MM-DD-YYYY (ASP standard). Parses the stored ISO form or any
// common entered form; returns the raw trimmed value if unparseable (never a
// guessed/wrong date).
export function formatDob(dob: string | null | undefined): string {
  if (!dob) return '—'
  const p = parseDobFlexible(dob)
  if (!p) return String(dob).trim() || '—'
  return `${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}-${p.y}`
}

// Parse a DOB from any common format into {y,m,d}. Accepts:
//   ISO        YYYY-MM-DD (optionally with a time suffix — the stored form)
//   delimited  M[M]/D[D]/YYYY or M[M]-D[D]-YYYY, and the 2-digit-year variants
//   compact    MMDDYYYY and MMDDYY
// 2-digit years pivot on the current year (> current ⇒ 1900s, else 2000s) since
// a DOB cannot be in the future. Returns null when unrecognizable. Intended to
// back multi-format DOB search as well as display.
export function parseDobFlexible(input: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!input) return null
  const s = String(input).trim()
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return normDob(+iso[1], +iso[2], +iso[3])
  const us = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2}|\d{4})$/)
  if (us) return normDob(expandDobYear(+us[3], us[3].length), +us[1], +us[2])
  const compact = s.match(/^(\d{2})(\d{2})(\d{2}|\d{4})$/)
  if (compact) return normDob(expandDobYear(+compact[3], compact[3].length), +compact[1], +compact[2])
  return null
}

function expandDobYear(y: number, len: number): number {
  if (len === 4) return y
  const cur = new Date().getFullYear() % 100
  return y > cur ? 1900 + y : 2000 + y
}

function normDob(y: number, m: number, d: number): { y: number; m: number; d: number } | null {
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2100) return null
  return { y, m, d }
}

// ── Canonical US date display — the platform standard ───────────────────────
// Storage / logs are UTC (ISO / DATETIMEOFFSET): sortable, queryable, date-math,
// and audit all depend on it. The UI formats ONLY for display, by two kinds:
//
//   INSTANT  — a real moment (entered, filled, promise, created/updated, audit,
//              credential times). Converted UTC -> the viewer's LOCAL time.
//              -> formatUsDateTime (date or date+time) / formatUsTimestamp (clock)
//   CALENDAR — a date with no time-of-day (DOB, date written, expiration, due,
//              lot exp). Rendered AS STORED, never tz-shifted — converting a
//              midnight-UTC date to local would move it a day (the DOB off-by-one).
//              -> formatUsDate
//
// Every date the UI shows should route through one of these. Rule of thumb:
// if shifting it by a timezone would be wrong (a birthday, a written date), it's
// CALENDAR; if it's "when did this happen", it's INSTANT.

const MONTHISH = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/

function localUsDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${mm}-${dd}-${d.getFullYear()}`
}

function localUs12h(d: Date): string {
  let h = d.getHours()
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${localUsDate(d)}, ${h}:${min} ${ampm}`
}

// CALENDAR date -> MM-DD-YYYY, rendered literally from the stored Y-M-D with NO
// timezone conversion. For DOB, date written, expiration, lot/due dates. Non-ISO
// input is returned trimmed (never guessed).
export function formatUsDate(value: string | null | undefined): string {
  if (value == null || value === '') return '—'
  const s = String(value)
  const m = s.match(MONTHISH)
  if (!m) return s.trim() || '—'
  return `${m[2]}-${m[3]}-${m[1]}`
}

// INSTANT (or a date stored as a datetime) -> the viewer's LOCAL time.
//   - real clock time present  -> MM-DD-YYYY, h:mm AM/PM (local)
//   - date-only or midnight UTC -> MM-DD-YYYY literal (calendar value, no shift)
// For entered / filled / promise and mixed grid columns. Non-ISO returned trimmed.
export function formatUsDateTime(value: string | null | undefined): string {
  if (value == null || value === '') return '—'
  const s = String(value)
  const m = s.match(MONTHISH)
  if (!m) return s.trim() || '—'
  const [, y, mo, d, hh, mm] = m
  // No time, or midnight: treat as a calendar date and render literally so a
  // date-only value never jumps a day when converted across timezones.
  if (!hh || (hh === '00' && mm === '00')) return `${mo}-${d}-${y}`
  const dt = new Date(s)
  return isNaN(dt.getTime()) ? `${mo}-${d}-${y}` : localUs12h(dt)
}

// A column name that denotes an IDENTITY, not a measure (rx_number, *_id, ndc, mrn, …). Kept in
// sync with the backend ReportSqlRunner.IsIdentifierName so grouping behaves the same client- and
// server-side: identifiers are COUNT(DISTINCT)ed, never SUMmed (summing an rx_number is nonsense).
// Word-part anchored so it matches ndc / ndc_code / dispensed_ndc but not an unrelated substring.
const IDENTIFIER_NAME_RE =
  /(^|_)(id|ids)($|_)|number|(^|_)ndc($|_)|(^|_)mrn($|_)|(^|_)npi($|_)|(^|_)dea($|_)|(^|_)ncpdp($|_)|(^|_)bin($|_)|(^|_)pcn($|_)|guid|uuid|barcode|serial|cardholder|(^|_)code($|_)|(^|_)zip($|_)|phone|fax/i

export function isIdentifierColumn(name: string | null | undefined): boolean {
  return !!name && IDENTIFIER_NAME_RE.test(name)
}

// NDC in standard 11-digit 5-4-2 billing format (e.g. 00168-0201-30). Strips any
// existing punctuation; an 11-digit value is hyphenated 5-4-2. Other lengths (already
// formatted, or a non-standard source) are returned trimmed, unchanged.
export function formatNdc(ndc: string | null | undefined): string {
  if (ndc == null || ndc === '') return '—'
  const d = String(ndc).replace(/[^0-9]/g, '')
  if (d.length === 11) return `${d.slice(0, 5)}-${d.slice(5, 9)}-${d.slice(9)}`
  return String(ndc).trim() || '—'
}

// True when a query reads as a phone number (or fragment, >=4 digits) and its digits
// appear in the stored phone's digits — matches regardless of stored formatting.
export function matchesPhone(query: string, phone: string | null | undefined): boolean {
  if (!phone) return false
  const t = (query ?? '').trim()
  if (!t || /[^0-9()\-. +]/.test(t)) return false
  const qd = t.replace(/\D/g, '')
  if (qd.length < 4) return false
  return String(phone).replace(/\D/g, '').includes(qd.length === 11 && qd[0] === '1' ? qd.slice(1) : qd)
}

// Generic cell formatter for dynamic tables/views (custom queues, report grids).
// dob/birth -> calendar MM-DD-YYYY; ndc -> 5-4-2; any other ISO value -> instant/local
// via formatUsDateTime (date-only values stay literal); everything else unchanged.
// NCPDP Prescription Origin Code (Telecommunication Standard field 419-DJ), which is what
// PioneerRx stores in rx_items.origin_type. The grid, the Rx detail card and the custom-view
// funnel all showed the bare digit ("3") — a code, not a fact a pharmacist can read. Unknown
// codes and non-numeric values pass through unchanged, so a tenant that already stores text
// ("Electronic") is not re-mapped.
const RX_ORIGIN_CODES: Record<string, string> = {
  '0': 'Not specified', '1': 'Written', '2': 'Telephone', '3': 'Electronic', '4': 'Fax', '5': 'Pharmacy',
}
export function formatRxOrigin(value: unknown): string {
  if (value == null || value === '') return '—'
  const s = String(value).trim()
  return RX_ORIGIN_CODES[s] ?? s
}
const ORIGIN_LABELS = new Set(['origin', 'rx origin', 'origin type', 'origin_type', 'prescription origin'])

export function formatCell(col: string, value: unknown): string {
  if (value == null || value === '') return '—'
  const lc = (col || '').toLowerCase()
  if (ORIGIN_LABELS.has(lc)) return formatRxOrigin(value)
  if (lc === 'dob' || lc.includes('birth')) return formatDob(value as string)
  if (lc.includes('ndc')) return formatNdc(value as string)
  if (lc.includes('phone') || lc.includes('fax')) return formatPhone(value as string)
  return formatUsDateTime(value as string)
}

// INSTANT with the clock always shown, in LOCAL time: MM-DD-YYYY, h:mm AM/PM.
// For log / feed / audit times where the time-of-day always matters.
export function formatUsTimestamp(value: string | null | undefined): string {
  if (value == null || value === '') return '—'
  const d = new Date(String(value))
  if (isNaN(d.getTime())) return String(value).trim() || '—'
  return localUs12h(d)
}

// INSTANT shown as its LOCAL calendar date only: MM-DD-YYYY. For created/updated
// "on" columns where the time-of-day is noise but local day is what's meant.
export function formatUsDateLocal(value: string | null | undefined): string {
  if (value == null || value === '') return '—'
  const d = new Date(String(value))
  if (isNaN(d.getTime())) return String(value).trim() || '—'
  return localUsDate(d)
}

/**
 * BA-11: DOB search in any common format — MMDDYYYY, MM/DD/YYYY, MM-DD-YYYY,
 * MM.DD.YYYY, M/D/YYYY, YYYYMMDD, MMDDYY. Compares digit-normalized input
 * against the stored ISO date string (no Date object — avoids TZ day-shifts).
 */
export function matchesDob(query: string, dob: string | null | undefined): boolean {
  if (!dob) return false
  const m = String(dob).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return false
  const [, yyyy, mm, dd] = m
  const digits = query.replace(/[^0-9]/g, '')
  if (digits.length === 8) return digits === mm + dd + yyyy || digits === yyyy + mm + dd
  if (digits.length === 6) return digits === mm + dd + yyyy.slice(2)
  // Separator formats with single-digit month/day (e.g. 1/5/1998)
  const parts = query.trim().split(/[\/\-.]/)
  if (parts.length === 3 && parts.every(p => /^\d{1,4}$/.test(p))) {
    const [a, b, c] = parts
    if (c.length === 4) return a.padStart(2, '0') === mm && b.padStart(2, '0') === dd && c === yyyy
    if (a.length === 4) return a === yyyy && b.padStart(2, '0') === mm && c.padStart(2, '0') === dd
  }
  return false
}
